import { logger } from '@automata/utils';
import { ActionTaskInput, Driver } from '@automata/wfb-api';
import { Action } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';

import {
  DriverActionArgumentConfig,
  DriverActionConfigsByActionNameByVersionedDriverName,
  DriverActionOutputConfig,
  WorkcellInstrument,
} from '../features/workflow/types';

import { getVersionedDriverName } from '.';

export const applyArgumentConfigToAction = ({
  action,
  args,
  outputs,
}: {
  action: Action;
  args: Record<string, DriverActionArgumentConfig>;
  outputs: Record<string, DriverActionOutputConfig>;
}): Action => {
  const newAction = cloneDeep(action);

  Object.entries(newAction.arguments).forEach(
    ([
      argumentName,
      // argument,
    ]) => {
      const argumentConfig = args[argumentName];
      if (!argumentConfig) return;

      const { hidden, alias, default: defaultValue } = argumentConfig;

      // NOTE(LT, 17/10/2024): to be handled later (its slightly fiddly to dealias arguments and we don't need it yet)
      // if (alias) newAction.arguments[alias] = argument;
      if (alias) logger.warn('de-aliasing arguments is not implemented');

      if (hidden) delete newAction.arguments[argumentName];

      const newArgument = newAction.arguments[argumentName];
      if (!isNil(defaultValue) && newArgument) {
        newArgument.default = defaultValue;
      }
    }
  );

  Object.entries(outputs).forEach(([name, { hidden }]) => {
    if (hidden) delete newAction.data_outputs[name];
  });

  return newAction;
};

export const applyActionAliasesToDriver = (
  driver: Driver,
  driverActionConfigsByActionNameByInstrumentId: DriverActionConfigsByActionNameByVersionedDriverName
): Driver => {
  const versionedDriverName = getVersionedDriverName(driver);
  const driverActionConfig =
    driverActionConfigsByActionNameByInstrumentId[versionedDriverName];
  if (!driverActionConfig) return driver;

  const existingDriverActions = cloneDeep(driver.actions);

  const newDriverActions = Object.entries(driverActionConfig).reduce<{
    [actionName: string]: Action;
  }>((acc, [actionName, driverActionConfigs]) => {
    const existingAction = existingDriverActions[actionName];

    if (!existingAction) {
      const message = `Action alias config found for non-existant action: "${actionName}"`;
      logger.warn(message);
      return acc;
    }

    const { aliases, args, hidden, outputs } = driverActionConfigs;

    const newAction: Action =
      args || outputs
        ? applyArgumentConfigToAction({
            action: existingAction,
            args: args ?? {},
            outputs: outputs ?? {},
          })
        : existingAction;

    acc[actionName] = newAction;

    if (aliases?.length && aliases.length > 0) {
      aliases.forEach(({ name }) => {
        if (acc[name]) {
          const message = `Overwriting existing driver action with alias: "${name}" on driver: "${versionedDriverName}"`;
          logger.warn(message);
        }
        acc[name] = newAction;
      });
    }

    // this should happen once all aliases have been applied
    if (hidden) {
      delete acc[actionName];
    }

    return acc;
  }, existingDriverActions);

  return {
    ...driver,
    actions: newDriverActions,
  };
};

type ActionTaskInputWithDiscriminator = ActionTaskInput & {
  type: 'action';
};

export const dealiasActionTaskInput = (
  actionTask: ActionTaskInputWithDiscriminator,
  driverActionConfigsByActionNameByVersionedDriverName: DriverActionConfigsByActionNameByVersionedDriverName,
  instrument: WorkcellInstrument
): ActionTaskInputWithDiscriminator => {
  const versionedDriverName = getVersionedDriverName({
    name: instrument.driverName,
    version: instrument.driverVersion,
  });

  const driverActionConfigsByActionName =
    driverActionConfigsByActionNameByVersionedDriverName[versionedDriverName];

  if (!driverActionConfigsByActionName) return actionTask;

  // TODO(LT, 10/10/2024): maybe there is a more efficient way to do this
  for (const [actionName, driverActionConfigs] of Object.entries(
    driverActionConfigsByActionName
  )) {
    const {
      aliases,
      // args // NOTE(LT, 17/10/2024): to be handled later
    } = driverActionConfigs;

    if (aliases?.length) {
      for (const { name } of aliases) {
        if (name && actionTask.action === name) {
          return {
            ...actionTask,
            action: actionName,
          };
        }
      }
    }

    // NOTE(LT, 17/10/2024): this is incomplete and not needed yet
    // if (args) {
    //   for (const [argumentName, argumentConfig] of Object.entries(args)) {
    //     if (
    //       argumentConfig.alias &&
    //       actionTask.arguments &&
    //       argumentName in actionTask.arguments
    //     ) {
    //       return {
    //         ...actionTask,
    //         arguments: {
    //           ...actionTask.arguments,
    //           [argumentName]: actionTask.arguments.static.......
    //         },
    //       };
    //     }
    //   }
    // }
  }

  return actionTask;
};
